'use client';

import { Checkbox } from '@ahafood/ui/components/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@ahafood/ui/components/form';
import { Label } from '@ahafood/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@ahafood/ui/components/radio-group';
import { formatCurrency } from '@ahm/common-helpers';
import type { Control } from 'react-hook-form';
import { toast } from 'sonner';
import type { Customization } from '@/types/item';

interface CustomizationGroupProps {
  customization: Customization;
  control: Control<any>;
}

export function CustomizationGroup({
  customization,
  control,
}: CustomizationGroupProps) {
  // Radio (single-select) chỉ khi BẮT BUỘC chọn đúng 1.
  // Mọi case còn lại — kể cả "bắt buộc ≥1 nhưng max>1" — là checkbox (multi-select).
  const isMultiSelect = !(
    customization.max_permitted === 1 && customization.min_permitted === 1
  );

  return (
    <div className="mb-4" id={`customization-group-${customization._id}`}>
      <div className="flex flex-row items-center gap-2 px-4 py-2 text-sm font-semibold justify-between">
        <h2
          className="text-neutral-90 flex-1"
          data-testid="customize-option-group-title"
        >
          {customization.name}
        </h2>
        <span
          className="text-neutral-50"
          data-testid="customize-option-group-req"
        >
          {customization.min_permitted > 0 ? (
            <span className="flex flex-col text-right">
              Chọn ít nhất {customization.min_permitted}
              {customization.max_permitted > customization.min_permitted && (
                <span className="text-xs text-neutral-50 font-normal">
                  (Chọn tối đa {customization.max_permitted})
                </span>
              )}
            </span>
          ) : (
            <span className="flex flex-col text-right">
              Không bắt buộc{' '}
              <span className="text-xs text-neutral-50 font-normal">
                (Chọn tối đa {customization.max_permitted})
              </span>
            </span>
          )}
        </span>
      </div>

      <FormField
        control={control}
        name={`customizations.${customization._id}`}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              {isMultiSelect ? (
                <MultiSelectOptions
                  customization={customization}
                  field={field}
                />
              ) : (
                <SingleSelectOptions
                  customization={customization}
                  field={field}
                />
              )}
            </FormControl>
            <FormMessage className="px-4 pt-1" />
          </FormItem>
        )}
      />
    </div>
  );
}

function MultiSelectOptions({
  customization,
  field,
}: {
  customization: Customization;
  field: any;
}) {
  const fieldValue = Array.isArray(field.value) ? field.value : [];

  const handleCheckedChange = (checked: boolean, optionId: string) => {
    let newValue = [...fieldValue];
    if (checked) {
      if (
        newValue.length < customization.max_permitted &&
        !newValue.includes(optionId)
      ) {
        newValue.push(optionId);
      } else if (
        newValue.length >= customization.max_permitted &&
        !newValue.includes(optionId)
      ) {
        if (customization.max_permitted === 1) {
          // Nếu chỉ được chọn 1, chuyển sang option mới luôn
          newValue = [optionId];
        } else {
          toast.error(
            `Bạn đã chọn tối đa ${customization.max_permitted} tùy chọn cho ${customization.name}.`
          );
          return;
        }
      }
    } else {
      newValue = newValue.filter((id) => id !== optionId);
    }
    field.onChange(newValue);
  };

  return (
    <div className="flex flex-col gap-1" data-testid="customize-option-list">
      {customization.options?.map((option) => (
        <Label
          htmlFor={`option-${customization._id}-${option._id}`}
          key={option._id}
          className="flex flex-row flex-nowrap items-start gap-2 p-4 w-full bg-white"
          data-testid="customize-option-item"
        >
          <Checkbox
            id={`option-${customization._id}-${option._id}`}
            checked={fieldValue.includes(option._id)}
            onCheckedChange={(checked) =>
              handleCheckedChange(Boolean(checked), String(option._id))
            }
            data-testid="customize-option-control"
          />
          <span
            className="text-neutral-70 flex-1"
            data-testid="customize-option-label"
          >
            {option.name}
          </span>
          {option.price > 0 && (
            <span
              className="text-neutral-70 flex-none"
              data-testid="customize-option-extra-price"
            >
              +{formatCurrency(option.price)}
            </span>
          )}
        </Label>
      ))}
    </div>
  );
}

function SingleSelectOptions({
  customization,
  field,
}: {
  customization: Customization;
  field: any;
}) {
  const fieldValue = typeof field.value === 'string' ? field.value : '';

  return (
    <RadioGroup
      className="gap-px"
      value={fieldValue}
      onValueChange={field.onChange}
      data-testid="customize-option-list"
    >
      {customization.options?.map((option) => (
        <Label
          htmlFor={`option-${customization._id}-${option._id}`}
          key={option._id}
          className="flex flex-row flex-nowrap items-start gap-2 p-4 w-full bg-white"
          data-testid="customize-option-item"
        >
          <RadioGroupItem
            className="text-sm font-semibold"
            value={String(option._id)}
            id={`option-${customization._id}-${option._id}`}
            data-testid="customize-option-control"
          />
          <span
            className="text-neutral-70 flex-1"
            data-testid="customize-option-label"
          >
            {option.name}
          </span>
          {option.price > 0 && (
            <span
              className="text-neutral-70 flex-none"
              data-testid="customize-option-extra-price"
            >
              +{formatCurrency(option.price)}
            </span>
          )}
        </Label>
      ))}
    </RadioGroup>
  );
}
